import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../utils/apiClient';

const PersonaSelector = forwardRef(({ selectedPersona, onPersonaChange }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgents();
  }, []);

  // Expose loadAgents method to parent via ref
  useImperativeHandle(ref, () => ({
    refreshAgents: loadAgents
  }));

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/agents');
      
      if (!response.ok) {
        throw new Error('Failed to load agents');
      }
      
      const agentIds = await response.json();
      
      // Load config for each agent to get display info
      const agentPromises = agentIds.map(async (id) => {
        try {
          const configResponse = await apiFetch(`/api/agents/${id}/config`);
          if (!configResponse.ok) return null;
          const config = await configResponse.json();
          return {
            id: config.id,
            name: config.name,
            description: config.description,
            color: config.color || 'bg-gray-500',
            icon: config.icon || '🤖'
          };
        } catch (err) {
          console.error(`Failed to load config for ${id}:`, err);
          return null;
        }
      });
      
      const loadedPersonas = await Promise.all(agentPromises);
      setPersonas(loadedPersonas.filter(p => p !== null));
    } catch (error) {
      console.error('Failed to load agents:', error);
      // Fallback to default personas
      setPersonas([
        {
          id: 'po',
          name: 'Product Owner',
          description: 'Business requirements and user needs',
          color: 'bg-green-500',
          icon: '📊'
        },
        {
          id: 'architect',
          name: 'Architect',
          description: 'System design and technical decisions',
          color: 'bg-purple-500',
          icon: '🏗️'
        },
        {
          id: 'engineer',
          name: 'Engineer',
          description: 'Implementation and code quality',
          color: 'bg-blue-500',
          icon: '⚙️'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const currentPersona = personas.find(p => p.id === selectedPersona);

  return (
    <div className={`bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ${
      isExpanded ? 'w-80' : 'w-20'
    }`}>
      {/* Toggle Button */}
      <div className="p-4 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center hover:bg-gray-50 p-2 rounded-lg transition-colors"
          title={isExpanded ? 'Collapse menu' : 'Expand persona menu'}
        >
          {isExpanded ? (
            <span className="text-lg">✕</span>
          ) : (
            <span className="text-lg">👤</span>
          )}
        </button>
      </div>

      {/* Persona Selection */}
      <div className="p-4">
        {isExpanded ? (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Agent</h3>
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading agents...</p>
              </div>
            ) : personas.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No agents found</p>
            ) : (
              personas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => onPersonaChange(persona.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                    selectedPersona === persona.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full ${persona.color} flex items-center justify-center text-white text-sm`}>
                      {persona.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{persona.name}</h4>
                      <p className="text-xs text-gray-600 mt-1">{persona.description}</p>
                    </div>
                    {selectedPersona === persona.id && (
                      <div className="text-blue-500">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
            
            {/* Current Persona Info */}
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Active Agent</h4>
              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full ${currentPersona?.color} flex items-center justify-center text-white text-xs`}>
                  {currentPersona?.icon}
                </div>
                <span className="text-sm text-gray-600">{currentPersona?.name}</span>
              </div>
            </div>
          </div>
        ) : (
          /* Collapsed View */
          <div className="space-y-2">
            {personas.map((persona) => (
              <button
                key={persona.id}
                onClick={() => onPersonaChange(persona.id)}
                className={`w-full py-2 px-1 rounded-lg transition-all ${
                  selectedPersona === persona.id
                    ? 'bg-blue-100'
                    : 'hover:bg-gray-100'
                }`}
                title={persona.name}
              >
                <div className={`w-10 h-10 mx-auto rounded-full ${persona.color} flex items-center justify-center text-white shadow-sm ${
                  selectedPersona === persona.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}>
                  {persona.icon}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

PersonaSelector.displayName = 'PersonaSelector';

PersonaSelector.propTypes = {
  selectedPersona: PropTypes.string.isRequired,
  onPersonaChange: PropTypes.func.isRequired
};

export default PersonaSelector;